import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { osName, OsTypes } from 'react-device-detect';
import braintree from 'braintree-web';
import CardForm from './components/CardForm';
import styled from 'styled-components';
import useDebounce from '../../hooks/useDebounce';
import { braintreePurchase, setCardholderName, setFormValidity } from '../../store/checkout';
import { selectAnalyticsData, selectEventsData, setEventData, setEventDataList } from '../../store/events';
import { sendAnalyticsEvents } from '../../analytics/common';
import { EVENTS } from '../../analytics/events';
import { selectPlan, selectPlanCoupon, selectShowCheckout } from '../../store/plans';
import { selectToken } from '../../store/signup';
import { setCheckoutLoader } from '../../store/loader';
import { setAmplitudeUserProperties } from '../../analytics/amplitude';
import { CARD_BRANDS, CardIconsList } from './CheckoutScreen';

const GOOGLE_PAY_MERCHANT_ID = process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID;

const textAlign = navigator.language.startsWith('ar') ? 'right' : 'left';

const ApplePayButton = styled.div`
  width: 100%;
  max-width: 327px;
  //margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px;
  background-color: #000;
  background-origin: content-box;
  border-radius: 12px;
  padding: 16px 34px;
  position: relative;
  margin-top: ${({ $mt }) =>
    $mt ? `${+$mt}${typeof $mt === 'number' ? 'px' : ''}` : '0px'};

  .icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%,-50%);
  }

  button {
    border-radius: 12px;
  }

  @media screen and (min-width: 744px) {
    cursor: pointer;
  }
`;

const GooglePayButton = styled.div`
  width: 100%;
  max-width: 327px;
  //margin: 0 auto 20px;
  height: 56px;
  //margin-bottom: 24px;
  margin-top: ${({ $mt }) =>
    $mt ? `${+$mt}${typeof $mt === 'number' ? 'px' : ''}` : '0px'};

  @media screen and (min-width: 744px) {
    cursor: pointer;
  }

  .gpay-button.black.hover {
    background-color: #000000;
  }
`;

const Braintree = ({
   btToken,
   braintreeInstance,
   cardInstance,
   threeDSInstance,
   card,
   expiration,
   cvc,
   cardholder,
   planDetails,
   children,
   separatorElement,
   isIntroOffer,
   renderBrand,
 }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const paymentsClient = useRef();
  const googlePayInstance = useRef();
  const applePayInstance = useRef();
  const [cardBrand, setCardBrand] = useState('');
  const [isCard, setIsCard] = useState(null);
  const [isExp, setIsExp] = useState(null);
  const [isCvv, setIsCvv] = useState(null);
  const [isValid, setIsValid] = useState(false);
  const [name, setName] = useState('');
  const [showApplePay, setShowApplePay] = useState(false);
  const [showGooglePay, setShowGooglePay] = useState(false);
  const debouncedName = useDebounce(name, 500);
  const debouncedValidity = useDebounce(isValid, 500);
  const plan = useSelector(selectPlan);
  const token = useSelector(selectToken);
  const eventsData = useSelector(selectEventsData);
  const analyticsParams = useSelector(selectAnalyticsData);
  const isAppleDevice = osName === OsTypes.IOS || osName === OsTypes.MAC_OS;
  const switchFocusToRef = useRef(null);
  const googlePayButton = useRef(null);
  const showCheckout = useSelector(selectShowCheckout);
  const couponDetails = useSelector(selectPlanCoupon);
  const commonTypes = (type) => {
    switch (type) {
      case 'visa':
      case 'Visa': return 'visa';
      case 'Mastercard':
      case 'master-card': return 'mastercard';
      case 'American Express':
      case 'american-express': return 'amex';
      case 'Maestro':
      case 'maestro': return 'discover';
      default: return '';
    }
  };

  useEffect(() => {
    if (debouncedName) {
      dispatch(setCardholderName(debouncedName));
    }
  }, [debouncedName, dispatch]);

  useEffect(() => {
    const storedCardBrand = eventsData?.cardBrand;
    if (cardBrand && (!storedCardBrand || (storedCardBrand.toLowerCase() !== cardBrand.toLowerCase()))) {
      dispatch(setEventData({id: 'cardBrand', data: cardBrand}));
      sendAnalyticsEvents(EVENTS.cardMethodChosen, {...eventsData, 'cardBrand': cardBrand});
    }
  }, [cardBrand, eventsData, dispatch]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://pay.google.com/gp/p/js/pay.js';
    script.onload = () => {
      paymentsClient.current = new window.google.payments.api.PaymentsClient({
        environment: process.env.REACT_APP_ENV === 'stage' ? 'TEST' : 'PRODUCTION'
      });
    };
    document.body.appendChild(script);
  },[]);

  useEffect(() => {
    if (braintreeInstance) {
      if (isAppleDevice) {
        if (window.ApplePaySession?.canMakePayments?.()) {
          braintree.applePay.create(
            {
              client: braintreeInstance,
              // authorization: btToken,
            },
            (error, applePay) => {
              if (error) {
                console.error('Failed to create Apple payment:', error);
                return;
              }
              applePayInstance.current = applePay;
              setShowApplePay(true);
            }
          );
        }
      } else {
        if (paymentsClient.current) {
          braintree.googlePayment.create(
            {
              client: braintreeInstance,
              googlePayVersion: 2,
              googleMerchantId: GOOGLE_PAY_MERCHANT_ID,
            },
            (error, googlePay) => {
              if (error) {
                console.error('Failed to create google pay', error);
                return;
              }
              googlePayInstance.current = googlePay;

              paymentsClient.current.isReadyToPay({
                apiVersion: 2,
                apiVersionMinor: 0,
                allowedPaymentMethods: googlePay.createPaymentDataRequest().allowedPaymentMethods,
                existingPaymentMethodRequired: true
              }).then((isReadyToPay) => {
                setShowGooglePay(isReadyToPay.result);
              });
            }
          );
        }
      }
      braintree.hostedFields.create(
        {
          client: braintreeInstance,
          authorization: btToken,
          styles: {
            'input': {
              'font-size': '18px',
              'color': '#3393EC',
              'line-height': '24px',
              'font-weight': '300',
              'font-family': 'Open Sans, Segoe UI, sans-serif',
              'text-align': textAlign,
              'height': '28px',
            },
            'input::placeholder': {
              'font-weight': 'inherit',
              'font-size': 'inherit',
              'color': '#ADADAD',
              'line-height': 'inherit',
              'letter-spacing': '1.2px',
            },
            '.invalid': {
              'color': '#eb1c26',
            },
          },
          fields: {
            number: {
              container: card.current,
              placeholder: '0000 0000 0000 0000',
            },
            expirationDate: {
              container: expiration.current,
              placeholder: t('MM/YY'),
            },
            cvv: {
              container: cvc.current,
              placeholder: t('CVV'),
            },
          },
        },
        (error, hostedFieldsInstance) => {
          if (error) {
            console.error('Error creating card form:', error);
            return;
          }

          const switchFocusTo = (field) => {
            hostedFieldsInstance.focus(field);
          };

          switchFocusToRef.current = switchFocusTo;

          switchFocusTo('number');

          hostedFieldsInstance.on('cardTypeChange', (event) => {
            if (event.cards.length === 1) {
              const type = commonTypes(event.cards[0].type);
              setCardBrand(type);
            } else {
              setCardBrand('');
            }
          });

          hostedFieldsInstance.on('validityChange', (event) => {
            const field = event.fields[event.emittedBy];
            const valid = field.isValid;

            switch (event.emittedBy) {
              case 'number':
                setIsCard(valid);
                valid && switchFocusTo('expirationDate');
                break;
              case 'expirationDate':
                setIsExp(valid);
                valid && switchFocusTo('cvv');
                break;
              case 'cvv':
                setIsCvv(valid);
                valid && cardholder.current.focus();
                break;
              default: return;
            }
          });

          cardInstance.current = hostedFieldsInstance;
        }
      );
      braintree.threeDSecure.create(
        {
          client: braintreeInstance,
          version: '2',
        },
        (error, threeDSecure) => {
          if (error) {
            console.error('Error creating 3DS:', error);
            return;
          }

          threeDSInstance.current = threeDSecure;

          threeDSecure.on('lookup-complete', (data, next) => {
            // console.log('lookup: ', data);
            next();
          });
        }
      );
    }
  },[isAppleDevice, braintreeInstance, cardInstance, threeDSInstance, btToken, card, expiration, cvc, cardholder, t]);

  useEffect(() => {
    if (showGooglePay && googlePayButton.current) {
      const button = paymentsClient.current.createButton({
        buttonColor: 'black',
        buttonType: 'checkout',
        buttonLocale: 'en',
        buttonSizeMode: 'fill',
        onClick: () => {},
      });

      googlePayButton.current.appendChild(button);
    }
  }, [showGooglePay]);

  useEffect(() => {
    const handleFocus = () => {
      if (showCheckout && switchFocusToRef.current) {
        switchFocusToRef.current('number');
      }
    };
    handleFocus();
  }, [showCheckout]);

  useEffect(() => {
    setIsValid(isCard && isCvv && isExp);
  }, [isCard, isCvv, isExp, dispatch]);

  useEffect(() => {
    dispatch(setFormValidity(debouncedValidity));
  }, [debouncedValidity, dispatch]);

  const payViaApple = () => {
    const fullEventsData = {
      ...eventsData,
      payment_method: 'applepay',
    };
    sendAnalyticsEvents(EVENTS.subBuyTapped, fullEventsData);
    dispatch(setCheckoutLoader({ show: true, type: 'checkout' }));
    sendAnalyticsEvents(EVENTS.paymentAnimationScreen, fullEventsData);

    const paymentRequest = applePayInstance.current.createPaymentRequest({
      label: `Family Locator ${t(planDetails?.description)} plan`,
      currency: 'usd',
      total: {
        label: `Family Locator ${t(planDetails?.description)} plan`,
        amount: planDetails.periodPrice.toString()
      },
      displayItems: [
        {
          label: `Family Locator ${t(planDetails?.description)} plan`,
          amount: planDetails.periodPrice.toString(),
        },
      ],
      requestPayerName: true,
      requestPayerEmail: true,
    });

    const session = new window.ApplePaySession(3, paymentRequest);
    session.onvalidatemerchant = function (event) {
      applePayInstance.current.performValidation({
        validationURL: event.validationURL,
        displayName: 'Family Locator'
      }, function (err, merchantSession) {
        if (err) {
          dispatch(setCheckoutLoader({ show: false, type: '' }));
          console.error('Apple Pay failed to load.');
          return;
        }
        session.completeMerchantValidation(merchantSession);
      });
    };

    session.onpaymentauthorized = function (event) {
      // console.log('Your shipping address is:', event.payment.shippingContact);

      applePayInstance.current.tokenize({
        token: event.payment.token
      }, function (tokenizeErr, payload) {
        if (tokenizeErr) {
          dispatch(setCheckoutLoader({ show: false, type: '' }));
          console.error('Error tokenizing Apple Pay:', tokenizeErr);
          session.completePayment(window.ApplePaySession.STATUS_FAILURE);
          return;
        }

        const cardBrand = payload?.details?.cardType || eventsData.cardBrand || 'unknown';
        const localEventsData = {
          payment_method: 'applepay',
          cardBrand,
          card_brand: cardBrand,
        };
        const extendedEventsData = {
          ...eventsData,
          ...localEventsData,
        };
        setAmplitudeUserProperties({ 'Payments gateways': 'Braintree' });
        dispatch(setEventDataList(localEventsData));
        dispatch(
          braintreePurchase(
            payload.nonce,
            payload.binData,
            null,
            plan,
            token,
            analyticsParams,
            extendedEventsData,
            couponDetails.couponName,
          )
        );

        // If requested, address information is accessible in event.payment
        // and may also be sent to your server.
        // console.log('billingPostalCode:', event.payment.billingContact.postalCode);

        // After you have transacted with the payload.nonce,
        // call `completePayment` to dismiss the Apple Pay sheet.
        session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
      });
    };

    session.oncancel = () => {
      dispatch(setCheckoutLoader({ show: false, type: '' }));
    };

    session.begin();
  };

  const payViaGoogle = () => {
    const fullEventsData = {
      ...eventsData,
      payment_method: 'googlepay',
    };
    sendAnalyticsEvents(EVENTS.subBuyTapped, fullEventsData);
    dispatch(setCheckoutLoader({ show: true, type: 'checkout' }));
    sendAnalyticsEvents(EVENTS.paymentAnimationScreen, fullEventsData);

    const paymentDataRequest = googlePayInstance.current.createPaymentDataRequest({
      transactionInfo: {
        currencyCode: 'USD',
        totalPriceStatus: 'FINAL',
        totalPrice: planDetails.periodPrice.toString(),
      }
    });

    paymentsClient.current.loadPaymentData(paymentDataRequest)
      .then((paymentData) => {
        googlePayInstance.current.parseResponse(paymentData, (err, payload) => {
          if (err) {
            dispatch(setCheckoutLoader({ show: false, type: '' }));
            console.error('Failed to parse payment response', err);
            return;
          }
          const cardBrand = payload?.details?.cardType || eventsData.cardBrand || 'unknown';
          const localEventsData = {
            payment_method: 'googlepay',
            cardBrand,
            card_brand: cardBrand,
          };
          const extendedEventsData = {
            ...eventsData,
            ...localEventsData,
          };
          setAmplitudeUserProperties({ 'Payments gateways': 'Braintree' });
          dispatch(setEventDataList(localEventsData));
          dispatch(
            braintreePurchase(
              payload.nonce,
              payload.binData,
              null,
              plan,
              token,
              analyticsParams,
              extendedEventsData,
              couponDetails.couponName,
            )
          );
        })
      }).catch((err) => {
      console.error('Failed to pay via store', err);
      dispatch(setCheckoutLoader({ show: false, type: '' }));
    });
  };

  const GooglePayMethod = showGooglePay && (
		<>
			{!isIntroOffer && separatorElement}
			<GooglePayButton onClick={payViaGoogle} ref={googlePayButton} $mt={isIntroOffer && 24}/>
		</>
	);

  const ApplePayMethod = showApplePay && (
		<>
			{!isIntroOffer && separatorElement}
			<ApplePayButton onClick={payViaApple} $mt={isIntroOffer && 24}>
				<svg
					className={'icon'}
					width="51"
					height="22"
					viewBox="0 0 51 22"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M10.6694 4.9165C12.1668 4.9165 13.3161 5.493 14.1172 6.646L14.0273 6.70215C13.9674 6.73958 13.9038 6.78451 13.8364 6.83691C13.769 6.88932 13.6829 6.95671 13.5781 7.03906C13.4808 7.12142 13.3835 7.21875 13.2861 7.33105C13.1963 7.43587 13.099 7.55566 12.9941 7.69043C12.8893 7.81771 12.792 7.9637 12.7021 8.12842C12.6198 8.29313 12.5412 8.46908 12.4663 8.65625C12.3989 8.83594 12.3428 9.03809 12.2979 9.2627C12.2604 9.4873 12.2417 9.7194 12.2417 9.95898C12.2417 10.4382 12.3203 10.8874 12.4775 11.3066C12.6423 11.7259 12.8257 12.0628 13.0278 12.3174C13.2375 12.5645 13.4696 12.7853 13.7241 12.98C13.9787 13.1672 14.1696 13.2944 14.2969 13.3618C14.4242 13.4292 14.514 13.4704 14.5664 13.4854C14.5514 13.5452 14.5103 13.6763 14.4429 13.8784C14.3755 14.0731 14.2407 14.3763 14.0386 14.7881C13.8439 15.1999 13.623 15.5892 13.376 15.9561C13.1439 16.293 12.9492 16.5625 12.792 16.7646C12.6348 16.9593 12.4364 17.1764 12.1968 17.416C11.9647 17.6481 11.7214 17.8203 11.4668 17.9326C11.2197 18.0449 10.9539 18.1011 10.6694 18.1011C10.2876 18.1011 9.83089 17.985 9.29932 17.7529C8.77523 17.5208 8.28109 17.4048 7.81689 17.4048C7.3527 17.4048 6.83984 17.5246 6.27832 17.7642C5.7168 18.0037 5.26758 18.1235 4.93066 18.1235C4.66862 18.1235 4.41781 18.0711 4.17822 17.9663C3.94613 17.8615 3.71029 17.693 3.4707 17.4609C3.23861 17.2214 3.0402 16.9967 2.87549 16.7871C2.71077 16.5775 2.49365 16.2855 2.22412 15.9111C1.69255 15.1549 1.25456 14.2603 0.910156 13.2271C0.573242 12.1864 0.404785 11.1569 0.404785 10.1387C0.404785 9.32259 0.52832 8.58138 0.775391 7.91504C1.02995 7.24121 1.36312 6.69466 1.7749 6.27539C2.19417 5.85612 2.65837 5.53418 3.16748 5.30957C3.68408 5.07747 4.2194 4.96143 4.77344 4.96143C5.19271 4.96143 5.7168 5.0887 6.3457 5.34326C6.97461 5.59033 7.39388 5.71387 7.60352 5.71387C7.7832 5.71387 8.21745 5.58285 8.90625 5.3208C9.59505 5.05127 10.1828 4.9165 10.6694 4.9165ZM10.0518 3.49023C9.75977 3.84961 9.38916 4.14535 8.93994 4.37744C8.49821 4.60954 8.08268 4.72559 7.69336 4.72559C7.611 4.72559 7.53239 4.7181 7.45752 4.70312C7.43506 4.61328 7.42383 4.50846 7.42383 4.38867C7.42383 3.9694 7.51367 3.55013 7.69336 3.13086C7.88053 2.7041 8.10889 2.33724 8.37842 2.03027C8.69287 1.65592 9.08968 1.34521 9.56885 1.09814C10.048 0.851074 10.5047 0.720052 10.939 0.705078C10.9614 0.809896 10.9727 0.925944 10.9727 1.05322C10.9727 1.93669 10.6657 2.74902 10.0518 3.49023ZM19.4004 3.9082H24.8789C26.2852 3.9082 27.4277 4.3444 28.3066 5.2168C29.1855 6.08268 29.625 7.21875 29.625 8.625C29.625 10.0247 29.1725 11.1608 28.2676 12.0332C27.3691 12.9056 26.2038 13.3418 24.7715 13.3418H21.9199V18H19.4004V3.9082ZM21.9199 5.96875V11.3105H24.2051C25.11 11.3105 25.8132 11.0762 26.3145 10.6074C26.8158 10.1387 27.0664 9.47786 27.0664 8.625C27.0664 7.77865 26.8158 7.12435 26.3145 6.66211C25.8197 6.19987 25.1198 5.96875 24.2148 5.96875H21.9199ZM34.2441 16.3301C34.9408 16.3301 35.5267 16.125 36.002 15.7148C36.4837 15.2982 36.7246 14.7806 36.7246 14.1621V13.3711L34.332 13.5176C33.7461 13.5566 33.2904 13.7031 32.9648 13.957C32.6458 14.2044 32.4863 14.5332 32.4863 14.9434C32.4863 15.3665 32.6458 15.7051 32.9648 15.959C33.2904 16.2064 33.7168 16.3301 34.2441 16.3301ZM33.5117 18.166C32.5026 18.166 31.6725 17.8763 31.0215 17.2969C30.377 16.7174 30.0547 15.9655 30.0547 15.041C30.0547 14.123 30.3932 13.3971 31.0703 12.8633C31.7539 12.3294 32.7272 12.0234 33.9902 11.9453L36.7246 11.7891V11.0469C36.7246 10.5065 36.5521 10.0866 36.207 9.78711C35.8685 9.48763 35.3965 9.33789 34.791 9.33789C34.2246 9.33789 33.7624 9.46484 33.4043 9.71875C33.0462 9.97266 32.8281 10.3177 32.75 10.7539H30.4941C30.5462 9.77083 30.9661 8.97331 31.7539 8.36133C32.5482 7.74284 33.5931 7.43359 34.8887 7.43359C36.1712 7.43359 37.1966 7.74609 37.9648 8.37109C38.7396 8.99609 39.127 9.82943 39.127 10.8711V18H36.7832V16.3496H36.7344C36.4414 16.903 35.9987 17.3457 35.4062 17.6777C34.8138 18.0033 34.1823 18.166 33.5117 18.166ZM42.2031 21.8477C41.6758 21.8477 41.3568 21.8346 41.2461 21.8086V19.8848C41.3568 19.9108 41.5911 19.9238 41.9492 19.9238C42.4505 19.9238 42.8411 19.8197 43.1211 19.6113C43.401 19.403 43.6094 19.0612 43.7461 18.5859C43.7461 18.5794 43.7689 18.485 43.8145 18.3027C43.8665 18.1204 43.8893 18.0228 43.8828 18.0098L40.2012 7.64844H42.8574L45.2988 15.9297H45.3379L47.7793 7.64844H50.3379L46.5977 18.4102C46.1549 19.6927 45.6081 20.5846 44.957 21.0859C44.306 21.5938 43.388 21.8477 42.2031 21.8477Z"
						fill="white"
					/>
				</svg>
			</ApplePayButton>
		</>
	);

  return (
    <>
      {isIntroOffer && (
        <>
          {ApplePayMethod}
          {GooglePayMethod}
          <CardIconsList>{CARD_BRANDS.map(renderBrand)}</CardIconsList>
        </>
      )}
      <CardForm
        method={'braintree'}
        card={card}
        expiration={expiration}
        cardholder={cardholder}
        cvc={cvc}
        cardBrand={cardBrand}
        setName={setName}
        t={t}
      />
      {children}
      {!isIntroOffer && (
        <>
          {ApplePayMethod}
          {GooglePayMethod}
        </>
      )}
    </>
  );
};

export default Braintree;
